import { useState } from "react";
import {
	type EntityType,
	type OrgRole,
	type Permission,
	type Team,
	type TeamMember,
	api,
} from "../lib/api.ts";
import { readFailure } from "../lib/read-failure.ts";
import { useLoad } from "../lib/useLoad.ts";
/**
 * Our side of self-serve: the approval queue, lending assets, and who is in
 * each team.
 *
 * Ad accounts are exclusive by design, so the picker shows which team already
 * holds one rather than letting an admin discover the clash on save. The
 * database enforces it either way — this only makes the rule visible.
 */
import { Badge, Button, Card, InfoBanner, Page, Select, Table } from "../ui/index.ts";

const TYPES: Array<{ type: EntityType; label: string; exclusive?: boolean }> = [
	{ type: "tiktok_accounts", label: "Ad accounts", exclusive: true },
	{ type: "agents", label: "Agents" },
	{ type: "proxies", label: "Proxies" },
];

const ORG_ROLES: OrgRole[] = ["owner", "manager", "member"];

export function Teams() {
	const [teams, reload] = useLoad(() => api.teams());
	const [accounts] = useLoad(() => api.tiktokAccounts());
	const [agents] = useLoad(() => api.agents());
	const [proxies] = useLoad(() => api.proxies());
	const [lending, setLending] = useState<string | null>(null);
	const [showingPeople, setShowingPeople] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const holders = teams.state === "ready" ? teams.data.accountHolders : {};

	// `heldById` is what the exclusivity check compares on. Team names are not
	// unique — the slug carries a hash suffix precisely so two teams may share a
	// display name — so comparing names would let an account held by the *other*
	// "Acme" look like one this team already holds.
	const options: Record<
		EntityType,
		Array<{ id: string; label: string; heldBy?: string; heldById?: string }>
	> = {
		tiktok_accounts:
			accounts.state === "ready"
				? accounts.data.accounts.map((a) => ({
						id: a.id,
						label: `${a.name} · ${a.advertiserId}`,
						heldBy: holders[a.id]?.orgName,
						heldById: holders[a.id]?.orgId,
					}))
				: [],
		agents:
			agents.state === "ready"
				? agents.data.agents.map((a) => ({ id: a.id, label: `${a.name} · ${a.kind}` }))
				: [],
		proxies:
			proxies.state === "ready"
				? proxies.data.proxies.map((p) => ({ id: p.id, label: `${p.label} · ${p.host}:${p.port}` }))
				: [],
	};

	async function run(action: () => Promise<unknown>) {
		setBusy(true);
		setError(null);
		try {
			await action();
			reload();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setBusy(false);
		}
	}

	const list = teams.state === "ready" ? teams.data.teams : [];
	const waiting = list.filter((t) => t.status === "pending").length;

	return (
		<Page
			title={waiting > 0 ? `${waiting} waiting for review` : "Teams"}
			description="Teams sign themselves up; nothing reaches them until you approve. An ad account can be lent to one team at a time — proxies and agents can be shared."
		>
			{error && <InfoBanner variant="danger" title={error} />}
			{teams.state === "failed" &&
				(() => {
					// By the status: «visible to admins only» is a 403's sentence and no other's.
					const f = readFailure(teams.status, {
						what: "Teams",
						forbidden: "Teams are visible to platform admins only.",
					});
					return (
						<InfoBanner variant={f.variant} title={f.title}>
							{teams.why}
						</InfoBanner>
					);
				})()}

			<Table<Team>
				columns={[
					{
						id: "team",
						header: "Team",
						accessor: (t) => (
							<>
								<div className="font-medium text-content">{t.name}</div>
								{t.note && <div className="text-xs text-muted">{t.note}</div>}
								<div className="font-mono text-xs text-muted">{t.requestedBy}</div>
							</>
						),
					},
					{
						id: "status",
						header: "Status",
						accessor: (t) => <Badge variant={tone(t.status)}>{t.status}</Badge>,
					},
					{
						id: "people",
						header: "People",
						accessor: (t) => (
							<Button
								variant="ghost"
								size="sm"
								onClick={() => setShowingPeople(showingPeople === t.id ? null : t.id)}
							>
								{t.memberCount ?? 0}
							</Button>
						),
					},
					{ id: "holds", header: "Holds", accessor: (t) => summarise(t) },
					{
						id: "actions",
						header: "",
						accessor: (t) => (
							<div className="flex flex-wrap gap-2">
								{t.status === "pending" && (
									<>
										<Button
											size="sm"
											disabled={busy}
											onClick={() => void run(() => api.setTeamStatus(t.id, "active"))}
										>
											Approve
										</Button>
										<Button
											variant="ghost"
											size="sm"
											disabled={busy}
											onClick={() => void run(() => api.setTeamStatus(t.id, "rejected"))}
										>
											Reject
										</Button>
									</>
								)}
								{t.status === "active" && (
									<>
										<Button
											variant="outline"
											size="sm"
											onClick={() => setLending(lending === t.id ? null : t.id)}
										>
											{lending === t.id ? "Close" : "Assign assets"}
										</Button>
										<Button
											variant="ghost"
											size="sm"
											disabled={busy}
											onClick={() => void run(() => api.setTeamStatus(t.id, "suspended"))}
										>
											Suspend
										</Button>
									</>
								)}
								{(t.status === "suspended" || t.status === "rejected") && (
									<Button
										variant="ghost"
										size="sm"
										disabled={busy}
										onClick={() => void run(() => api.setTeamStatus(t.id, "active"))}
									>
										Reinstate
									</Button>
								)}
							</div>
						),
					},
				]}
				data={list}
				keyExtractor={(t) => t.id}
				failed={teams.state === "failed"}
				loading={teams.state === "loading"}
				emptyMessage="No teams have registered yet."
			/>

			{showingPeople && (
				// Keyed so switching to another team remounts and refetches its roster.
				<People
					key={showingPeople}
					team={list.find((t) => t.id === showingPeople)}
					busy={busy}
					onChange={(action) => void run(action)}
				/>
			)}

			{lending && (
				<LendEditor
					key={lending}
					team={list.find((t) => t.id === lending)}
					options={options}
					busy={busy}
					onSave={(p) => void run(() => api.setTeamGrants(lending, p))}
				/>
			)}
		</Page>
	);
}

/**
 * Who is in a team. Loaded on demand rather than with the table — most of the
 * time you want the count, and pulling every roster to render one number would
 * be a query per team on every visit.
 */
function People({
	team,
	busy,
	onChange,
}: {
	team?: Team;
	busy: boolean;
	onChange: (action: () => Promise<unknown>) => void;
}) {
	const orgId = team?.id ?? "";
	const [members, reload] = useLoad(() =>
		orgId ? api.teamMembers(orgId) : Promise.resolve({ members: [] }),
	);
	if (!team) return null;

	function act(action: () => Promise<unknown>) {
		onChange(async () => {
			await action();
			reload();
		});
	}

	return (
		<Card className="mt-6 p-5">
			<p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-muted">
				People in {team.name}
			</p>
			{members.state === "failed" && <InfoBanner variant="danger" title={members.why} />}
			<Table<TeamMember>
				columns={[
					{
						id: "who",
						header: "Person",
						accessor: (m) => (
							<>
								<div className="font-medium text-content">{m.email ?? "—"}</div>
								<div className="font-mono text-xs text-muted">{m.userId}</div>
							</>
						),
					},
					{
						id: "role",
						header: "Role",
						accessor: (m) => (
							<Select
								value={m.role}
								disabled={busy}
								onChange={(e) =>
									act(() => api.setTeamMember(orgId, m.userId, { role: e.target.value as OrgRole }))
								}
								options={ORG_ROLES.map((r) => ({ value: r, label: r }))}
							/>
						),
					},
					{
						id: "status",
						header: "Status",
						accessor: (m) => (
							<Badge variant={m.status === "active" ? "success" : "warning"}>{m.status}</Badge>
						),
					},
					{
						id: "actions",
						header: "",
						accessor: (m) =>
							m.role === "owner" ? null : (
								<Button
									variant="ghost"
									size="sm"
									disabled={busy}
									onClick={() => act(() => api.removeTeamMember(orgId, m.userId))}
								>
									Remove
								</Button>
							),
					},
				]}
				data={members.state === "ready" ? members.data.members : []}
				keyExtractor={(m) => m.id}
				failed={members.state === "failed"}
				loading={members.state === "loading"}
				emptyMessage="Nobody yet — unusual, since registering seats the owner."
			/>
			<p className="mt-3 text-xs text-muted">
				The owner registered the team and cannot be removed here — change their role first if they
				are leaving.
			</p>
		</Card>
	);
}

function LendEditor({
	team,
	options,
	busy,
	onSave,
}: {
	team?: Team;
	options: Record<
		EntityType,
		Array<{ id: string; label: string; heldBy?: string; heldById?: string }>
	>;
	busy: boolean;
	onSave: (p: { entity_type: EntityType; permission: Permission; entity_ids: string[] }) => void;
}) {
	const [type, setType] = useState<EntityType>("tiktok_accounts");
	const [permission, setPermission] = useState<Permission>("write");
	if (!team) return null;

	const selected = new Set(
		(team.grants ?? [])
			.filter((g) => g.entityType === type && g.permission === permission)
			.map((g) => g.entityId),
	);
	const spec = TYPES.find((t) => t.type === type);
	const list = options[type];

	function toggle(id: string) {
		const next = new Set(selected);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		onSave({ entity_type: type, permission, entity_ids: [...next] });
	}

	return (
		<Card className="mt-6 p-5">
			<p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-muted">
				Assets for {team.name}
			</p>
			<div className="flex flex-wrap items-end gap-3">
				<Select
					label="Type"
					value={type}
					onChange={(e) => setType(e.target.value as EntityType)}
					options={TYPES.map((t) => ({ value: t.type, label: t.label }))}
				/>
				<Select
					label="Permission"
					value={permission}
					onChange={(e) => setPermission(e.target.value as Permission)}
					options={[
						{ value: "read", label: "Can view" },
						{ value: "write", label: "Can use" },
					]}
				/>
			</div>

			{list.length === 0 ? (
				<p className="mt-4 text-sm text-muted">Nothing of this type exists yet to assign.</p>
			) : (
				<div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-2">
					{list.map((o) => {
						// By id, never by name — see the note on `heldById` above.
						const takenElsewhere =
							spec?.exclusive && o.heldById && o.heldById !== team.id && !selected.has(o.id);
						return (
							<label
								key={o.id}
								className={`flex items-start gap-2 text-sm ${
									takenElsewhere ? "check-off" : "cursor-pointer"
								}`}
							>
								<input
									type="checkbox"
									className="mt-1"
									checked={selected.has(o.id)}
									disabled={busy || Boolean(takenElsewhere)}
									onChange={() => toggle(o.id)}
								/>
								<span>
									{o.label}
									{takenElsewhere && <span className="text-warning"> — held by {o.heldBy}</span>}
								</span>
							</label>
						);
					})}
				</div>
			)}
			<p className="mt-4 text-xs text-muted">
				{spec?.exclusive
					? "One ad account belongs to one team at a time; take it back before lending it on."
					: "Shareable — assign to as many teams as needed."}{" "}
				Each tick saves immediately.
			</p>
		</Card>
	);
}

function tone(status: string): "success" | "warning" | "error" {
	if (status === "active") return "success";
	if (status === "rejected") return "error";
	return "warning";
}

function summarise(t: Team): string {
	const g = t.grants ?? [];
	if (g.length === 0) return "nothing yet";
	return TYPES.map((spec) => {
		const n = new Set(g.filter((x) => x.entityType === spec.type).map((x) => x.entityId)).size;
		return n ? `${n} ${spec.label.toLowerCase()}` : null;
	})
		.filter(Boolean)
		.join(" · ");
}
